import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { savedFiltersAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Bookmark,
  Plus,
  Trash2,
  Share2,
  Lock,
  User,
  Loader2,
  Filter,
  ChevronRight,
  Edit2,
  Check,
  X,
} from 'lucide-react';

export function SavedFiltersSidebar({
  isOpen,
  onClose,
  page, // 'dashboard' | 'salary_reports' | 'advances'
  currentFilters, // Current filter state from the page
  onApplyFilter, // Callback to apply a saved filter
  filterLabels, // Object mapping filter keys to display labels
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [personalFilters, setPersonalFilters] = useState([]);
  const [sharedFilters, setSharedFilters] = useState([]);
  
  // Save new filter state
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');
  const [newFilterShared, setNewFilterShared] = useState(false);
  
  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  
  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadFilters();
    }
  }, [isOpen, page]);

  const loadFilters = async () => {
    try {
      setLoading(true);
      const response = await savedFiltersAPI.getAll(page);
      setPersonalFilters(response.data.personal || []);
      setSharedFilters(response.data.shared || []);
    } catch (error) {
      console.error('Failed to load filters:', error);
      toast.error('Failed to load saved filters');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFilter = async () => {
    if (!newFilterName.trim()) {
      toast.error('Please enter a filter name');
      return;
    }

    try {
      setSaving(true);
      await savedFiltersAPI.create({
        name: newFilterName.trim(),
        page: page,
        filter_config: currentFilters,
        is_shared: newFilterShared,
      });
      toast.success('Filter saved successfully');
      setNewFilterName('');
      setNewFilterShared(false);
      setShowSaveForm(false);
      loadFilters();
    } catch (error) {
      console.error('Failed to save filter:', error);
      toast.error(error.response?.data?.detail || 'Failed to save filter');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateFilter = async (filterId, filterConfig) => {
    try {
      await savedFiltersAPI.update(filterId, { filter_config: filterConfig });
      toast.success('Filter updated with current settings');
      loadFilters();
    } catch (error) {
      console.error('Failed to update filter:', error);
      toast.error(error.response?.data?.detail || 'Failed to update filter');
    }
  };

  const handleRenameFilter = async (filterId) => {
    if (!editName.trim()) {
      toast.error('Please enter a filter name');
      return;
    }

    try {
      await savedFiltersAPI.update(filterId, { name: editName.trim() });
      toast.success('Filter renamed');
      setEditingId(null);
      setEditName('');
      loadFilters();
    } catch (error) {
      console.error('Failed to rename filter:', error);
      toast.error(error.response?.data?.detail || 'Failed to rename filter');
    }
  };

  const handleToggleShared = async (filter) => {
    try {
      await savedFiltersAPI.update(filter.id, { is_shared: !filter.is_shared });
      toast.success(filter.is_shared ? 'Filter is now private' : 'Filter is now shared');
      loadFilters();
    } catch (error) {
      console.error('Failed to update filter:', error);
      toast.error(error.response?.data?.detail || 'Failed to update filter');
    }
  };

  const handleDeleteFilter = async () => {
    if (!deleteConfirmId) return;
    
    try {
      await savedFiltersAPI.delete(deleteConfirmId);
      toast.success('Filter deleted');
      setDeleteConfirmId(null);
      loadFilters();
    } catch (error) {
      console.error('Failed to delete filter:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete filter');
    }
  };

  const renderFilterConfig = (config) => {
    if (!config || !filterLabels) return null;
    
    const items = [];
    for (const [key, value] of Object.entries(config)) {
      if (!filterLabels[key]) continue;
      
      let displayValue = value;
      if (Array.isArray(value)) {
        if (value.length === 0) continue;
        displayValue = `${value.length} selected`;
      } else if (value === 'all' || value === '') {
        continue;
      }
      
      items.push(
        <span key={key} className="text-xs text-muted-foreground">
          {filterLabels[key]}: <span className="font-medium">{displayValue}</span>
        </span>
      );
    }
    
    return items.length > 0 ? (
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
        {items}
      </div>
    ) : (
      <span className="text-xs text-muted-foreground italic">No filters applied</span>
    );
  };

  const FilterCard = ({ filter, isOwner }) => {
    const isEditing = editingId === filter.id;
    
    return (
      <div 
        data-testid={`saved-filter-${filter.id}`}
        className="group p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <div className="flex items-center gap-2">
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="h-7 text-sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRenameFilter(filter.id);
                    if (e.key === 'Escape') {
                      setEditingId(null);
                      setEditName('');
                    }
                  }}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => handleRenameFilter(filter.id)}
                >
                  <Check className="h-3 w-3" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7"
                  onClick={() => {
                    setEditingId(null);
                    setEditName('');
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm truncate">{filter.name}</span>
                {filter.is_shared && (
                  <Badge variant="secondary" className="text-xs px-1.5 py-0">
                    <Share2 className="h-3 w-3 mr-1" />
                    Shared
                  </Badge>
                )}
                {!isOwner && (
                  <Badge variant="outline" className="text-xs px-1.5 py-0">
                    <User className="h-3 w-3 mr-1" />
                    {filter.user_name}
                  </Badge>
                )}
              </div>
            )}
            {!isEditing && renderFilterConfig(filter.filter_config)}
          </div>
          
          {!isEditing && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {isOwner && (
                <>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => {
                      setEditingId(filter.id);
                      setEditName(filter.name);
                    }}
                    title="Rename"
                  >
                    <Edit2 className="h-3 w-3" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => handleToggleShared(filter)}
                    title={filter.is_shared ? 'Make private' : 'Share with team'}
                  >
                    {filter.is_shared ? (
                      <Lock className="h-3 w-3" />
                    ) : (
                      <Share2 className="h-3 w-3" />
                    )}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => handleUpdateFilter(filter.id, currentFilters)}
                    title="Update with current filters"
                  >
                    <Filter className="h-3 w-3" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => setDeleteConfirmId(filter.id)}
                    title="Delete"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
        
        {!isEditing && (
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-2 h-7"
            onClick={() => {
              onApplyFilter(filter.filter_config);
              toast.success(`Applied filter: ${filter.name}`);
            }}
          >
            <ChevronRight className="h-3 w-3 mr-1" />
            Apply Filter
          </Button>
        )}
      </div>
    );
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="right" className="w-[400px] sm:max-w-[400px] p-0">
          <SheetHeader className="p-6 pb-4">
            <SheetTitle className="flex items-center gap-2">
              <Bookmark className="h-5 w-5" />
              Saved Filters
            </SheetTitle>
            <SheetDescription>
              Save and apply frequently used filter combinations
            </SheetDescription>
          </SheetHeader>
          
          <div className="px-6 pb-4">
            {showSaveForm ? (
              <div className="space-y-3 p-3 rounded-lg border bg-muted/30">
                <div className="space-y-2">
                  <Label htmlFor="filter-name">Filter Name</Label>
                  <Input
                    id="filter-name"
                    data-testid="save-filter-name-input"
                    placeholder="e.g., My Organizations"
                    value={newFilterName}
                    onChange={(e) => setNewFilterName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveFilter();
                    }}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="filter-shared" className="text-sm">Share with team</Label>
                  <Switch
                    id="filter-shared"
                    data-testid="save-filter-shared-toggle"
                    checked={newFilterShared}
                    onCheckedChange={setNewFilterShared}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid="save-filter-confirm-btn"
                    className="flex-1"
                    size="sm"
                    onClick={handleSaveFilter}
                    disabled={saving}
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-1" />
                        Save
                      </>
                    )}
                  </Button>
                  <Button
                    data-testid="save-filter-cancel-btn"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowSaveForm(false);
                      setNewFilterName('');
                      setNewFilterShared(false);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                data-testid="save-current-filter-btn"
                variant="outline"
                className="w-full"
                onClick={() => setShowSaveForm(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Save Current Filters
              </Button>
            )}
          </div>
          
          <Separator />
          
          <ScrollArea className="h-[calc(100vh-280px)]">
            <div className="p-6 space-y-6">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  {/* Personal Filters */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      My Filters
                      {personalFilters.length > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {personalFilters.length}
                        </Badge>
                      )}
                    </h3>
                    {personalFilters.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        No saved filters yet. Save your current filters to access them quickly.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {personalFilters.map((filter) => (
                          <FilterCard key={filter.id} filter={filter} isOwner={true} />
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Shared Filters */}
                  {sharedFilters.length > 0 && (
                    <div className="space-y-3">
                      <h3 className="text-sm font-medium flex items-center gap-2">
                        <Share2 className="h-4 w-4" />
                        Shared by Team
                        <Badge variant="secondary" className="text-xs">
                          {sharedFilters.length}
                        </Badge>
                      </h3>
                      <div className="space-y-2">
                        {sharedFilters.map((filter) => (
                          <FilterCard key={filter.id} filter={filter} isOwner={false} />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Saved Filter?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The saved filter will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFilter}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
